import axios from 'axios';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import {
  ActionRecord,
  AuditRecord,
  CreatedAuditRecord,
  Loadable,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { Order, Shipment } from '@/model';

export interface UseActionLogResponse {
  actionRecords: ComputedRef<Loadable<ActionRecord[]>>;
  auditRecords: ComputedRef<Loadable<AuditRecord[]>>;
  createdAuditRecord: ComputedRef<CreatedAuditRecord>;
  loadActionAndAuditRecords: () => Promise<void>;
}

export type ActionLoggableEntity = Order | Shipment;

export const useActionLog = (
  entity: MaybeRefOrGetter<ActionLoggableEntity>,
): UseActionLogResponse => {
  const { t } = useI18n();
  const toast = useToast();

  const _actionRecords: Ref<Loadable<ActionRecord[]>> = ref({ loading: false });
  const _auditRecords: Ref<Loadable<AuditRecord[]>> = ref({ loading: false });

  const isOrder = computed(() => toValue(entity) instanceof Order);

  const actionRecords = computed(() => _actionRecords.value);
  const auditRecords = computed(() => _auditRecords.value);

  const findActionRecords = computed(() => {
    const findActions = isOrder.value
      ? api.actionLog.findOrderActions
      : api.actionLog.findShipmentActions;
    return findActions.bind(api.actionLog);
  });

  const findAuditRecords = computed(() =>
    isOrder.value
      ? api.order.findOrderAudits.bind(api.order)
      : api.shipment.findShipmentAudits.bind(api.shipment),
  );

  const createdAuditRecord = computed(() => {
    const rawEntity = toValue(entity);

    return new CreatedAuditRecord({
      createdBy: rawEntity.createdBy,
      createdDate: rawEntity.createdDate,
      source: rawEntity.source,
    });
  });

  const loadActionRecords = async () => {
    const rawEntity = toValue(entity);

    try {
      if (!rawEntity.id) {
        return;
      }

      _actionRecords.value.loading = true;
      _actionRecords.value.data = await findActionRecords.value(rawEntity.id);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      _actionRecords.value.error = e;
      console.error('Error loading action records', e);
      toast.addError({
        error: e,
        detail: t('errors.loadingParam', {
          param: t('audits.actionRecords', 2),
        }),
      });
    } finally {
      _actionRecords.value.loading = false;
    }
  };

  const loadAuditRecords = async () => {
    const rawEntity = toValue(entity);

    try {
      if (!rawEntity.id) {
        return;
      }

      _auditRecords.value.loading = true;
      _auditRecords.value.data = await findAuditRecords.value(rawEntity.id);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      _auditRecords.value.error = e;
      console.error('Error loading audit records', e);
      toast.addError({
        error: e,
        detail: t('errors.loadingParam', {
          param: t('audits.auditRecords', 2),
        }),
      });
    } finally {
      _auditRecords.value.loading = false;
    }
  };

  const loadActionAndAuditRecords = async () => {
    Promise.allSettled([loadActionRecords(), loadAuditRecords()]);
  };

  return {
    actionRecords,
    auditRecords,
    createdAuditRecord,
    loadActionAndAuditRecords,
  };
};
